import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import field from '@sorare/core/src/assets/icons/field.svg';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BodyS,
  BrandS,
  BrandXL,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { AnyRewardConfig } from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText';
import { Progress } from '@sorare/core/src/components/rivals/branded/Progress';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import { TaskCampaignLabel } from '#src/components/missions/Streaks/TaskCampaignLabel/index.tsx';
import { TaskIcon } from '#src/components/missions/TaskIcon/index.tsx';

import type { TasksDetails_thresholdsStreakTask } from './__generated__/index.graphql.ts';

const CornerBadgeWrapper = styled(Vertical).attrs({ gap: 0.5 })`
  position: absolute;
  align-items: flex-start;
  left: calc(-1 * var(--half-unit) - 1px);
  top: var(--unit);
`;
const DropdownContent = styled(Vertical).attrs({ gap: 1.5, center: true })`
  position: relative;
  padding: var(--double-unit);
  border: 1px solid var(--c-rewards-orange);
  border-radius: var(--intermediate-unit);
  background:
    linear-gradient(to top, var(--c-black) 50%, var(--c-nl-700)),
    linear-gradient(80deg, rgba(255, 78, 34, 0.8) 20%, rgba(255, 78, 34, 0)),
    linear-gradient(rgba(255, 78, 34, 0.3), rgba(255, 78, 34, 0.1)),
    linear-gradient(to top, var(--c-nl-800), var(--c-nl-900), transparent),
    no-repeat center / cover url(${field}),
    linear-gradient(
      to bottom,
      rgba(var(--c-rgb-black), 0.8),
      rgba(var(--c-rgb-black), 0.9),
      rgba(var(--c-rgb-black), 0.8)
    ),
    var(--c-rewards-orange);
  box-shadow: 0 0 20px rgba(255, 78, 34, 0.3) inset;
`;
const StyledSBHorizontal = styled(SBHorizontal)`
  width: 100%;
`;
const ProgressContainer = styled(Horizontal)`
  width: 100%;
  max-width: 160px;
`;
const LineThrough = styled.span`
  text-decoration-line: line-through;
  color: var(--c-nd-600);
`;

const Score = styled(BrandXL)`
  text-shadow: 0 0 10px var(--c-white);

  &.text-gradient-color {
    text-shadow: none;
  }
`;

type Props = {
  task: TasksDetails_thresholdsStreakTask;
};
export const TaskDetails = ({ task }: Props) => {
  if (!task) {
    return null;
  }

  const { rewardTaskCampaign, scoreTaskCampaign, currentThreshold } = task;
  const { originalRewardConfigs, rewardConfigs } = currentThreshold;

  return (
    <DropdownContent>
      <CornerBadgeWrapper>
        {[rewardTaskCampaign, scoreTaskCampaign].map(
          taskCampaign =>
            taskCampaign && (
              <TaskCampaignLabel
                key={taskCampaign.slug}
                taskCampaign={taskCampaign}
              />
            )
        )}
      </CornerBadgeWrapper>
      <Horizontal>
        <Score
          className={scoreTaskCampaign ? 'text-gradient-color' : undefined}
          style={
            scoreTaskCampaign
              ? ({
                  '--text-gradient-color': `linear-gradient(323deg, ${scoreTaskCampaign.color1}, ${scoreTaskCampaign.color2})`,
                } as CSSProperties)
              : undefined
          }
        >
          {task.currentThreshold.score}
        </Score>
        <TaskIcon size={3} task={task} />
      </Horizontal>
      <BrandS>
        <FormattedMessage {...fantasy.hotStreak} />
      </BrandS>
      <ProgressContainer
        style={
          {
            '--height': 'var(--unit)',
            '--active-color': 'var(--c-white)',
          } as CSSProperties
        }
      >
        <Progress
          progress={task.progress || 0}
          target={task.target}
          color="var(--c-nd-200)"
          activeColor="var(--c-white)"
          highlightNextColor="var(--c-nd-400)"
          showLabel={false}
        />
        <LabelS bold>
          {task.progress || 0}/{task.target}
        </LabelS>
      </ProgressContainer>
      <BodyS>{task.description}</BodyS>
      <StyledSBHorizontal grow>
        <LabelS bold uppercase color="var(--c-onBase-success)">
          <FormattedMessage {...glossary.rewards} />
        </LabelS>
        <Horizontal>
          {rewardConfigs.map(reward => {
            const associatedOriginalReward = originalRewardConfigs?.find(
              ({ __typename }) => __typename === reward.__typename
            );
            return (
              <AnyRewardConfig
                key={reward.id}
                anyRewardConfig={reward}
                customText={
                  !!associatedOriginalReward &&
                  !!rewardTaskCampaign &&
                  (({ rewardText }) => (
                    <Horizontal gap={0.5}>
                      <span
                        className="text-gradient-color"
                        style={
                          {
                            '--text-gradient-color': `linear-gradient(323deg, ${rewardTaskCampaign?.color1}, ${rewardTaskCampaign?.color2})`,
                          } as CSSProperties
                        }
                      >
                        {rewardText}
                      </span>
                      <LineThrough>
                        <RewardConfigText
                          anyRewardConfig={associatedOriginalReward}
                        />
                      </LineThrough>
                    </Horizontal>
                  ))
                }
              />
            );
          })}
        </Horizontal>
      </StyledSBHorizontal>
    </DropdownContent>
  );
};

TaskDetails.fragments = {
  task: gql`
    fragment TasksDetails_thresholdsStreakTask on ThresholdsStreakTask {
      id
      progress
      target
      description(forNextLineup: true)
      currentThreshold(forNextLineup: true) {
        originalRewardConfigs {
          id
          ...AnyRewardConfig_anyRewardConfigInterface
        }
        rewardConfigs {
          id
          ...AnyRewardConfig_anyRewardConfigInterface
        }
        score
      }
      scoreTaskCampaign {
        slug
        ...TaskCampaignLabel_taskCampaign
      }
      rewardTaskCampaign {
        slug
        ...TaskCampaignLabel_taskCampaign
      }
      ...TaskIcon_task
    }
    ${TaskIcon.fragments.task}
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${TaskCampaignLabel.fragments.taskCampaign}
  ` as TypedDocumentNode<TasksDetails_thresholdsStreakTask>,
};
